import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Tooltip,
  Select,
  DatePicker,
  Form,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import BulkOrderDeleteModal from '../delete/BulkOrderDeleteModal';
import '../../order/orders/Orders.css';
import { canOrderDelete, canOrderEdit, canOrderCreate } from '../../../utils/userRoles';

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const DATE_FORMAT = 'YYYY-MM-DD';

const MODE_OF_PAYMENT_OPTIONS = [
  { value: 'PayPal', label: 'PayPal' },
  { value: 'Razorpay', label: 'Razorpay' },
  { value: 'PayGlocal', label: 'PayGlocal' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Others', label: 'Others' },
];

const ORDER_DISPATCHED_OPTIONS = [
  { value: 'inprogress', label: 'In progress' },
  { value: 'half', label: 'Half' },
  { value: 'full', label: 'Full' },
];

const DISPATCHED_COLORS = { inprogress: 'orange', half: 'blue', full: 'green' };
const ORDER_DISPATCHED_LABELS = { inprogress: 'In progress', half: 'Half', full: 'Full' };

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const emptyFilters = () => ({
  modeOfPayment: undefined,
  orderDispatched: undefined,
  orderDateStart: undefined,
  orderDateEnd: undefined,
  targetDateStart: undefined,
  targetDateEnd: undefined,
});

const BULK_ORDERS_LIST_STORAGE_KEY = 'kosha_bulk_orders_list_v1';

function serializeFiltersForStorage(filters) {
  const f = { ...emptyFilters(), ...filters };
  const toDateStr = (d) => (d && dayjs(d).isValid() ? dayjs(d).format(DATE_FORMAT) : undefined);
  return {
    modeOfPayment: f.modeOfPayment,
    orderDispatched: f.orderDispatched,
    orderDateStart: toDateStr(f.orderDateStart),
    orderDateEnd: toDateStr(f.orderDateEnd),
    targetDateStart: toDateStr(f.targetDateStart),
    targetDateEnd: toDateStr(f.targetDateEnd),
  };
}

function deserializeFiltersFromStorage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = emptyFilters();
  if (raw.modeOfPayment && typeof raw.modeOfPayment === 'string' && MODE_OF_PAYMENT_OPTIONS.some((o) => o.value === raw.modeOfPayment)) {
    out.modeOfPayment = raw.modeOfPayment;
  }
  if (raw.orderDispatched && typeof raw.orderDispatched === 'string' && ORDER_DISPATCHED_OPTIONS.some((o) => o.value === raw.orderDispatched)) {
    out.orderDispatched = raw.orderDispatched;
  }
  const parseD = (s) => {
    if (s == null || typeof s !== 'string') return undefined;
    const d = dayjs(s, DATE_FORMAT, true);
    return d.isValid() ? d : undefined;
  };
  out.orderDateStart = parseD(raw.orderDateStart);
  out.orderDateEnd = parseD(raw.orderDateEnd);
  out.targetDateStart = parseD(raw.targetDateStart);
  out.targetDateEnd = parseD(raw.targetDateEnd);
  return out;
}

function loadBulkOrdersListPrefs() {
  try {
    const raw = localStorage.getItem(BULK_ORDERS_LIST_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o.v !== 1 || !o.filters || typeof o.filters !== 'object') return null;
    const filters = deserializeFiltersFromStorage(o.filters);
    if (!filters) return null;
    const search = typeof o.search === 'string' ? o.search : '';
    const pageSize = [10, 20, 50].includes(Number(o.pageSize)) ? Number(o.pageSize) : DEFAULT_PAGE_SIZE;
    const current = typeof o.current === 'number' && o.current >= 1 ? Math.floor(o.current) : 1;
    return { filters, search, pageSize, current };
  } catch {
    return null;
  }
}

function saveBulkOrdersListPrefs(filters, searchApplied, paginationSlice) {
  try {
    localStorage.setItem(
      BULK_ORDERS_LIST_STORAGE_KEY,
      JSON.stringify({
        v: 1,
        filters: serializeFiltersForStorage(filters),
        search: searchApplied ?? '',
        pageSize: paginationSlice.pageSize,
        current: paginationSlice.current,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

export default function BulkOrdersList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authReducer);
  const canCreate = user?.roles?.some((r) => r.value === 'admin' || r.value === 'manager');

  const resolveInitialListState = () => {
    const saved = loadBulkOrdersListPrefs();
    if (saved) {
      return {
        filters: saved.filters,
        searchInput: saved.search,
        searchApplied: saved.search,
        pagination: { current: saved.current, pageSize: saved.pageSize, total: 0 },
      };
    }
    return {
      filters: emptyFilters(),
      searchInput: '',
      searchApplied: '',
      pagination: { current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 },
    };
  };

  const initialList = resolveInitialListState();
  const lastDebouncedSearchRef = useRef(initialList.searchApplied);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(initialList.pagination);
  const [searchInput, setSearchInput] = useState(initialList.searchInput);
  const [searchApplied, setSearchApplied] = useState(initialList.searchApplied);
  const [appliedFilters, setAppliedFilters] = useState(initialList.filters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState(initialList.filters);
  const [toDelete, setToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchList = useCallback(
    async (page, limit, search, filters = {}) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (search && search.trim()) params.search = search.trim();
        if (filters.modeOfPayment) params.modeOfPayment = filters.modeOfPayment;
        if (filters.orderDispatched) params.orderDispatched = filters.orderDispatched;
        if (filters.orderDateStart) params.orderDateStart = dayjs(filters.orderDateStart).format(DATE_FORMAT);
        if (filters.orderDateEnd) params.orderDateEnd = dayjs(filters.orderDateEnd).format(DATE_FORMAT);
        if (filters.targetDateStart) params.targetDateStart = dayjs(filters.targetDateStart).format(DATE_FORMAT);
        if (filters.targetDateEnd) params.targetDateEnd = dayjs(filters.targetDateEnd).format(DATE_FORMAT);
        const res = await axios.get('/bulk-orders', { params });
        setList(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to load bulk orders';
        dispatch(setMessage({ message: msg, type: 'error' }));
        setList([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchList(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
  }, [pagination.current, pagination.pageSize, searchApplied, appliedFilters, fetchList]);

  useEffect(() => {
    saveBulkOrdersListPrefs(appliedFilters, searchApplied, {
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [appliedFilters, searchApplied, pagination.current, pagination.pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      const searchChanged = next !== lastDebouncedSearchRef.current;
      lastDebouncedSearchRef.current = next;
      setSearchApplied(next);
      if (searchChanged) {
        setPagination((prev) => ({ ...prev, current: 1 }));
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openFilterModal = () => {
    setModalFilters({ ...appliedFilters });
    setFilterModalOpen(true);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...modalFilters });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    setModalFilters(emptyFilters());
    setAppliedFilters(emptyFilters());
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const isEmptyFilterValue = (v) =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  const hasActiveFilters = Object.values(appliedFilters).some((v) => !isEmptyFilterValue(v));

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const handleDeleteConfirm = async () => {
    if (!toDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/bulk-orders/${toDelete._id}`);
      dispatch(setMessage({ message: 'Bulk order deleted successfully', type: 'success' }));
      setToDelete(null);
      fetchList(pagination.current, pagination.pageSize, searchApplied, appliedFilters);
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: 'Bulk Order #',
      dataIndex: 'bulkOrderNumber',
      key: 'bulkOrderNumber',
      width: 160,
      render: (val, record) => (
        <span
          className="orders-cell-id orders-cell-id-link"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/bulk-orders/${record._id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/bulk-orders/${record._id}`);
            }
          }}
        >
          {val || '—'}
        </span>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (val) => <span className="orders-cell-customer">{val || '—'}</span>,
    },
    {
      title: 'Order amount',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      width: 110,
      render: (val) => (
        <span className="orders-cell-amount">
          {val != null ? `₹${Number(val).toLocaleString('en-IN')}` : '—'}
        </span>
      ),
    },
    {
      title: 'Mode of payment',
      dataIndex: 'modeOfPayment',
      key: 'modeOfPayment',
      width: 120,
      render: (s) => <Tag className="orders-tag-source">{s || '—'}</Tag>,
    },
    {
      title: 'Concerned person',
      dataIndex: 'concernedPerson',
      key: 'concernedPerson',
      width: 130,
      render: (val) => <span>{val || '—'}</span>,
    },
    {
      title: 'Order date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 110,
      render: formatDate,
    },
    {
      title: 'Target date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      width: 110,
      render: formatDate,
    },
    {
      title: 'Dispatched',
      dataIndex: 'orderDispatched',
      key: 'orderDispatched',
      width: 100,
      render: (s) => (
        <Tag color={DISPATCHED_COLORS[s] || 'default'} className="orders-tag-status">
          {s ? (ORDER_DISPATCHED_LABELS[s] ?? String(s).charAt(0).toUpperCase() + String(s).slice(1)) : 'In progress'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/bulk-orders/${record._id}`)}
            />
          </Tooltip>
          {canOrderEdit(user) && (
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/bulk-orders/${record._id}/edit`)}
              />
            </Tooltip>
          )}
          {canOrderDelete(user) && (
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                className="app-btn-delete"
                onClick={() => setToDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="orders-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="orders-header">
          <div className="orders-header-title-row">
            <Title level={3} style={{ margin: 0 }}>
              Bulk Orders
            </Title>
            <div className="orders-header-actions">
              <Button
                icon={<FilterOutlined />}
                onClick={openFilterModal}
                className="orders-filter-btn"
                type="default"
              >
                Filter
              </Button>
              {canOrderCreate(user) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/bulk-orders/new')}
                  className="orders-add-btn app-btn-primary"
                >
                  New bulk order
                </Button>
              )}
            </div>
          </div>
          <Input
            placeholder="Search by customer, bulk order # or concerned person"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
            className="orders-search-input"
          />
        </div>

        <Card className="orders-card">
          <Table
            className="orders-table"
            columns={columns}
            dataSource={list.map((row) => ({ ...row, key: row._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} bulk orders`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            responsive
            scroll={{ x: 1000 }}
          />
        </Card>

        <Modal
          title="Filter bulk orders"
          open={filterModalOpen}
          onCancel={() => setFilterModalOpen(false)}
          footer={[
            <Button key="clear" onClick={clearFilters} className="orders-filter-clear-btn">
              Clear
            </Button>,
            <Button key="cancel" onClick={() => setFilterModalOpen(false)} className="app-btn-cancel">
              Cancel
            </Button>,
            <Button key="apply" type="primary" onClick={applyFilters} className="app-btn-primary">
              Apply
            </Button>,
          ]}
          width={520}
          destroyOnClose
        >
          <div className="orders-filter-modal-content">
            <Form layout="vertical" className="orders-filter-form">
              <Form.Item label="Mode of payment">
                <Select
                  placeholder="All"
                  allowClear
                  value={modalFilters.modeOfPayment}
                  onChange={(v) => setModalFilters((f) => ({ ...f, modeOfPayment: v }))}
                  options={MODE_OF_PAYMENT_OPTIONS}
                  className="orders-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Order dispatched">
                <Select
                  placeholder="All"
                  allowClear
                  value={modalFilters.orderDispatched}
                  onChange={(v) => setModalFilters((f) => ({ ...f, orderDispatched: v }))}
                  options={ORDER_DISPATCHED_OPTIONS}
                  className="orders-filter-select"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Order date">
                <Space.Compact style={{ width: '100%' }}>
                  <DatePicker
                    placeholder="Start"
                    value={modalFilters.orderDateStart ? dayjs(modalFilters.orderDateStart) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateStart: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                  <DatePicker
                    placeholder="End"
                    value={modalFilters.orderDateEnd ? dayjs(modalFilters.orderDateEnd) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, orderDateEnd: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                </Space.Compact>
              </Form.Item>
              <Form.Item label="Target date">
                <Space.Compact style={{ width: '100%' }}>
                  <DatePicker
                    placeholder="Start"
                    value={modalFilters.targetDateStart ? dayjs(modalFilters.targetDateStart) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, targetDateStart: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                  <DatePicker
                    placeholder="End"
                    value={modalFilters.targetDateEnd ? dayjs(modalFilters.targetDateEnd) : null}
                    onChange={(d) => setModalFilters((f) => ({ ...f, targetDateEnd: d || undefined }))}
                    format={DATE_FORMAT}
                    style={{ width: '50%' }}
                  />
                </Space.Compact>
              </Form.Item>
            </Form>
          </div>
        </Modal>

        <BulkOrderDeleteModal
          open={toDelete}
          onCancel={() => setToDelete(null)}
          onConfirm={handleDeleteConfirm}
          bulkOrder={toDelete}
          loading={deleteLoading}
        />
      </Space>
    </div>
  );
}
